import React, { useState } from "react";
import { toast } from "sonner";

import { useCreateInterface, useSaveCredentials } from "@/shared/api/interfaces";
import { FieldLabel, GhostButton, PrimaryButton, TextInput } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { FormError } from "./FormError";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkedAppsWarning } from "./LinkedAppsWarning";
import { Steps } from "./Steps";

import type { Interface } from "@/shared/types";
import type { SetupProps } from "../types";

const portal = (
  <a
    href="https://discord.com/developers/applications"
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium text-[var(--text-primary)] underline underline-offset-2"
  >
    Developer Portal
  </a>
);

const NEW_BOT = [
  <>Open the Discord {portal} and choose New Application.</>,
  <>Open Bot in the sidebar and turn on Message Content Intent.</>,
  <>Press Reset Token, copy it and paste it below.</>,
];

const NEW_TOKEN = [
  <>Open your application in the Discord {portal}.</>,
  <>Open Bot, then press Reset Token and copy the new one.</>,
  <>Paste the token below.</>,
];

/** Discord's answers, in this dialog's words. */
const WORDS = {
  3110001: "Discord rejected that token. Copy it again from Bot → Reset Token.",
  3110002: "That bot is already connected to another agent.",
  3110003: "Couldn't reach Discord. Try again.",
  3110010: "Too many attempts. Wait a few minutes and try again.",
};

/**
 * Connect a Discord bot by its token, or give a broken one a new token.
 *
 * Like the Telegram dialog, the token is read from the field when it is sent
 * and never held in React state. Unlike it, a bot is useless until it is in a
 * server, so a successful connect does not close the dialog: it offers the
 * invite link, and Done closes it.
 */
export function DiscordSetup({ agent, existing, onClose }: SetupProps) {
  const create = useCreateInterface();
  const save = useSaveCredentials();
  const write = existing ? save : create;
  const [filled, setFilled] = useState(false);
  const [connected, setConnected] = useState<Interface | null>(null);
  const meta = catalogFor("discord")!;

  const connect = async (values: Record<string, string>) => {
    const credentials = { bot_token: (values.bot_token ?? "").trim() };
    try {
      const done = existing
        ? await save.send({ id: existing.id, credentials })
        : await create.send({ agent_id: agent.id, type: "discord", credentials });
      toast.success(`${done.label ?? meta.name} is connected`);
      setConnected(done);
    } catch {
      // FormError shows it under the field, from the mutation's own state.
    }
  };

  if (connected && existing) {
    return (
      <InterfaceDialog
        meta={meta}
        title={`${connected.label ?? meta.name} is reconnected`}
        description={`${agent.name} is answering on Discord again.`}
        onClose={onClose}
        footer={<PrimaryButton type="button" onClick={onClose}>Done</PrimaryButton>}
      >
        {connected.setup.invite_url && (
          <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
            Need it in another server?{" "}
            <a
              href={connected.setup.invite_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[var(--text-primary)] underline underline-offset-2"
            >
              Add to your server
            </a>
          </p>
        )}
      </InterfaceDialog>
    );
  }

  if (connected) {
    return (
      <InterfaceDialog
        meta={meta}
        title={`${connected.label ?? meta.name} is connected`}
        description={`One more step: add the bot to your server so people can message ${agent.name} there.`}
        onClose={onClose}
        footer={<PrimaryButton type="button" onClick={onClose}>Done</PrimaryButton>}
      >
        {connected.setup.invite_url && (
          <a
            href={connected.setup.invite_url}
            target="_blank"
            rel="noopener noreferrer"
            className="press inline-flex h-9 items-center rounded-[10px] bg-[var(--surface)] px-3.5 text-[13px] font-medium text-[var(--text-primary)] ring-1 ring-[var(--hairline-strong)] hover:bg-[var(--accent-hover)]"
          >
            Add to your server
          </a>
        )}
        <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
          It answers DMs and @mentions at once. Pick channels it should answer in full from the interface's menu,
          under Bot channels.
        </p>
      </InterfaceDialog>
    );
  }

  return (
    <InterfaceDialog
      meta={meta}
      title={existing ? `Reconnect ${existing.label ?? meta.name}` : "Connect Discord"}
      description={existing?.error ?? meta.desc}
      onSubmit={connect}
      onClose={onClose}
      footer={
        <>
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton type="submit" disabled={!filled || write.isPending}>
            {write.isPending ? "Connecting…" : "Connect"}
          </PrimaryButton>
        </>
      }
    >
      <Steps items={existing ? NEW_TOKEN : NEW_BOT} />
      <div>
        <FieldLabel>Bot token</FieldLabel>
        <TextInput
          name="bot_token"
          aria-label="Bot token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder="MTEw…"
          onChange={(value) => {
            setFilled(!!value.trim());
            if (write.error) write.reset();
          }}
        />
      </div>
      <FormError error={write.error} words={WORDS} />
      {!existing && <LinkedAppsWarning agent={agent} />}
    </InterfaceDialog>
  );
}
